import {
  BillingPortalLink,
  ConsentGrantRequest,
  EntitlementResponse,
  ForgotPasswordPayload,
  LoginExternalApplicationPayload,
  LoginPayload,
  LoginResponse,
  RegisterDevicePayload,
  SignupPayload,
  SignupRedemption,
  Status,
  SubscriptionResponse,
  TotpSetupResult,
  TwoFactorStatus,
  VerifyTwoFactorPayload,
  WebAuthnCredentialSummary,
} from '@/types'
import { MFA_CLIENT_CAPABILITIES } from '@/constants/Common'
import { startAssertion, startRegistration } from '@/utils/webauthn'
import { useMutation } from '@tanstack/react-query'
import httpService from './httpService'
import axios from 'axios'

const authPath = '/auth/v1.0'
const authDevicePath = '/auth/v1.0/device'
const loginPath = `${authPath}/login`
const signupPath = '/auth/v1.1/signup'
const authEmailInitiate = '/auth/password/v1.0/initiate'
const authEmailReset = '/auth/password/v1.0/reset'
const authEmailResetWithToken = '/auth/password/v1.0/reset/token'
const externalApplicationLogin = `${authPath}/login-read-only-token`
const consentGrantPath = `${authPath}/consent/grant`
const twoFactorVerifyPath = `${authPath}/2fa/verify`
const twoFactorResendPath = `${authPath}/2fa/resend`
const twoFactorStatusPath = `${authPath}/2fa/status`
const totpSetupPath = `${authPath}/2fa/totp/setup`
const totpEnablePath = `${authPath}/2fa/totp/enable`
const totpDisablePath = `${authPath}/2fa/totp`
const webAuthnRegisterOptionsPath = `${authPath}/2fa/webauthn/register/options`
const webAuthnRegisterPath = `${authPath}/2fa/webauthn/register`
const webAuthnCredentialsPath = `${authPath}/2fa/webauthn/credentials`
const webAuthnCredentialPath = (credentialId: string) =>
  `${authPath}/2fa/webauthn/${encodeURIComponent(credentialId)}`
const webAuthnAuthenticateOptionsPath = `${authPath}/2fa/webauthn/authenticate/options`
const webAuthnVerifyPath = `${authPath}/2fa/webauthn/verify`
const twoFactorRecoveryCodesPath = `${authPath}/2fa/recovery-codes`

/**
 * Advertises the strong second factors this client can actually perform, so the API offers the
 * intersection of (what the user has enrolled) and (what this client can do) — a browser can run a
 * WebAuthn ceremony and render a TOTP QR/code box, so it sends both. The server treats this purely
 * as an advisory hint (it never widens what it will accept); it just avoids offering a factor this
 * client can't complete. The old `mfa-challenge` token predates strong MFA and the server ignores
 * unknown tokens, so it effectively advertised nothing.
 */
export const CLIENT_CAPABILITIES = MFA_CLIENT_CAPABILITIES
const MFA_CAPABILITY_HEADERS = { 'X-DUPR-Client-Capabilities': CLIENT_CAPABILITIES }
const authEmailEVerifyPath = '/auth/email/v1.0/verify'
const clientPath = '/client/v1.0'
const entitlements = '/entitlements'
export const activeSubscriptions = '/subscription/active'

export const login = async (payload: LoginPayload) => {
  try {
    return await httpService.Post<LoginResponse>(loginPath, payload)
  } catch (err) {
    // A stale first-party session cookie rides this request (HttpOnly, so JS cannot drop it before
    // sending). When it resolves to an inactive/invalid account the server rejects with 401 AND
    // expires the cookie, so retrying once evaluates the credentials on their own — yielding the
    // normal challenge/session — instead of the login staying blocked by the dead session. A valid
    // cookie never 401s here (the login just re-issues the session), so this only fires for a
    // stale one.
    if ((err as { response?: { status?: number } })?.response?.status === 401) {
      return await httpService.Post<LoginResponse>(loginPath, payload)
    }
    throw err
  }
}

export const signup = async (payload: SignupPayload) => {
  return await httpService.Post<LoginResponse>(signupPath, payload)
}

const signupStartPath = '/auth/v1.0/signup/start'
const signupRedeemPath = '/auth/v1.0/signup/redeem'

/**
 * Email-first signup entry. The server always answers the same "check your inbox", registered or
 * not, so callers show that message unconditionally and never branch on the outcome. Bare axios:
 * anonymous by nature, must not carry a stale bearer.
 */
export const startSignup = async (email: string) => {
  const res = await axios.post(signupStartPath, { email }, { timeout: 30000 })
  return res.data
}

/** Redeems the opened signup link for the finalize token + verified email. Single use. */
export const redeemSignup = async (signupToken: string): Promise<SignupRedemption> => {
  const res = await axios.post<{ result?: SignupRedemption }>(signupRedeemPath, { signupToken }, { timeout: 30000 })
  if (!res.data?.result?.finalizeToken) throw new Error('Invalid signup redemption response')
  return res.data.result
}

export const registerDevice = async (payload: RegisterDevicePayload) => {
  return await httpService.Put(authDevicePath, payload)
}

// Callers deliberately discard both the result and any failure: the reset flow must look the
// same for a registered and an unregistered address. See forgot-password.tsx.
export const initiateEmail = async (email?: string) => {
  return await httpService.Get<{ message: string; status: Status }>(
    `${authEmailInitiate}?email=${encodeURIComponent(email!)}`
  )
}

export const forgotPassword = async (payload: ForgotPasswordPayload) => {
  const { email, code, password } = payload
  const headers = {
    'x-password': password,
  }
  return await httpService.Get<{ status: Status }>(
    `${authEmailReset}?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`,
    headers
  )
}

// Completes a password reset from an emailed reset link. The signed token goes in the body; the
// new password is supplied via the x-password header (same convention as the code-based reset).
// For an emailed-link token the body is a bare SUCCESS. For the token the login halt carried (see
// PasswordResetRequiredRequirement) the same call also FINISHES the login — the body is the 2FA
// verify shape (`result`) and the session lands on the HttpOnly cookies.
export const resetPasswordWithToken = async ({ token, password }: { token: string; password: string }) => {
  return await httpService.Post<LoginResponse>(authEmailResetWithToken, { token }, { 'x-password': password })
}

export const useLoginExternalApplication = () => {
  return useMutation([], async (payload: LoginExternalApplicationPayload) => {
    const { partnerId, ...requestPayload } = payload
    const res = await axios
      .create()
      .post<LoginResponse>(externalApplicationLogin, requestPayload, {
        headers: { 'x-authorization': partnerId, ...MFA_CAPABILITY_HEADERS },
        timeout: 30000,
      })
      .then((r) => r.data)
    return res
  })
}

export const useGrantConsent = () => {
  return useMutation([], async (payload: ConsentGrantRequest) => {
    const res = await axios
      .create()
      .post<LoginResponse>(consentGrantPath, payload, { timeout: 30000 })
      .then((r) => r.data)
    return res
  })
}

export const useEntitlements = () => {
  return useMutation([], async (token: string) => {
    const res = await httpService.Get(entitlements, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return res as EntitlementResponse
  })
}
export const useSubscriptions = () => {
  return useMutation([], async () => {
    const res = await httpService.Post(activeSubscriptions, undefined)
    return res as SubscriptionResponse
  })
}
export const useOauthSubscriptions = () => {
  return useMutation([], async (request: { token: string }) => {
    return await axios
      .create()
      .post<SubscriptionResponse>(activeSubscriptions, null, { headers: { Authorization: `Bearer ${request.token}` } })
      .then((res) => res.data)
  })
}

export const useStripePortalSession = () => {
  return useMutation([], async () => {
    const res = await httpService.Post('/subscription/manage', undefined)
    return res as BillingPortalLink
  })
}

export const getClientNameByKey = async (clientKey: string, token: string) => {
  if (!clientKey) throw new Error('Client key is required')
  if (token) {
    const response = await httpService.Get<{ status: string; result: string }>(`${clientPath}/${clientKey}/name`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    return response.result
  }
}

/**
 * Redeems the token from an emailed link -- an address verification, or a SIGN-IN, in which case
 * `result` is a full session.
 *
 * Deliberately the bare `axios`, NOT httpService. This exchange is unauthenticated by nature, and
 * httpService attaches whatever bearer redux-persist still holds. After a mass logout that is a
 * dead token in every browser that was ever signed in: the server refuses it with 401 before the
 * route's permitAll is even consulted, the 401 handler sees a stored refresh token and tries a
 * refresh, that is refused too, and logUserOut() fires -- so clicking a perfectly valid sign-in
 * link ended on the login screen behind a "Session Expired" toast. The refresh call has its own
 * instance for the same reason. Same pattern as the external-login calls below.
 */
export const useUserVerifyEmail = () => {
  return useMutation([], async (payload: { token?: string }) => {
    // Verifies an email address from an emailed verification link (e.g. after an email change). The
    // emailed sign-in LINK is retired, so this only ever verifies addresses now. Bare axios:
    // anonymous by nature, must not carry a stale bearer.
    const res = await axios.post<{ status: Status; message: string }>(authEmailEVerifyPath, payload, {
      timeout: 30000,
    })
    return res.data
  })
}

/**
 * Exchanges a 2FA challenge token plus the emailed code for a real session.
 *
 * The response shape is identical to an ordinary login, so callers finish through the same
 * `completeLogin` path they already use.
 */
export const useVerifyTwoFactor = () => {
  // `method` selects the factor being proven: omit for the legacy emailed code, or send the strong
  // kind (e.g. { type: 'urn:dupr:second-factor:totp' }) to verify against an enrolled authenticator.
  return useMutation([], async (payload: VerifyTwoFactorPayload) => {
    const res = await axios
      .create()
      .post<LoginResponse>(twoFactorVerifyPath, payload, {
        headers: { ...MFA_CAPABILITY_HEADERS },
        // Required so the browser stores the first-party session cookie this response sets
        // (a cross-origin Set-Cookie is only honored for a credentialed request).
        withCredentials: true,
        timeout: 30000,
      })
      .then((r) => r.data)
    return res
  })
}

/** Sends a fresh code for an existing challenge. */
export const useResendTwoFactor = () => {
  return useMutation([], async (payload: { challengeToken: string }) => {
    const res = await axios
      .create()
      .post(twoFactorResendPath, payload, {
        headers: { ...MFA_CAPABILITY_HEADERS },
        withCredentials: true,
        timeout: 30000,
      })
      .then((r) => r.data)
    return res
  })
}

// ==================== Strong-MFA: TOTP + WebAuthn (authenticated, cookie session) ====================
// All of these ride the first-party HttpOnly session cookie via httpService (never a bearer). They
// return the Result<T> envelope { status, results:[T], errors, message }, except the WebAuthn
// *options* endpoints which answer RAW WebAuthn JSON. Enrolment/removal carry a step-up `password`
// in the body (a stolen session alone cannot add or remove a login factor).

/** The `Result<T>` envelope the strong-MFA endpoints answer with. */
type ResultEnvelope<T> = {
  status?: string
  results?: T[]
  errors?: Array<{ message?: string }>
}

/**
 * Unwraps a `Result<T>` envelope, throwing the server's message on a FAILURE status. These endpoints
 * report a business failure (wrong code, wrong password, expired setup) as HTTP 200 with
 * `status: FAILURE`, which axios resolves — so without this a mutation's `onSuccess` fires for a
 * failure and the page announces success for something that did not happen.
 */
const unwrapResult = <T>(data: ResultEnvelope<T>, fallback: string): T[] => {
  if (data?.status !== 'SUCCESS') {
    throw new Error(data?.errors?.[0]?.message || fallback)
  }
  return data.results ?? []
}

/** The account's 2FA status, incl. whether an authenticator (TOTP) is enrolled. */
export const getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
  const res = await httpService.Get<ResultEnvelope<TwoFactorStatus>>(twoFactorStatusPath)
  return unwrapResult(res, 'Could not load your 2FA settings.')[0]
}

/** Begin authenticator enrolment: returns the otpauth URI (QR) + base32 secret to confirm. */
export const useTotpSetup = () => {
  return useMutation([], async (payload: { password: string }) => {
    const res = await httpService.Post<ResultEnvelope<TotpSetupResult>>(totpSetupPath, payload)
    return unwrapResult(res, 'Could not start authenticator setup.')[0]
  })
}

/** Confirm authenticator enrolment with a current 6-digit code. */
export const useTotpEnable = () => {
  return useMutation([], async (payload: { code: string }) =>
    unwrapResult(await httpService.Post<ResultEnvelope<string>>(totpEnablePath, payload), 'That code was not accepted.')
  )
}

/** Disable the authenticator — requires the account password step-up (mirrors TOTP setup). */
export const useTotpDisable = () => {
  return useMutation([], async (payload: { password: string }) =>
    unwrapResult(
      await httpService.Delete<ResultEnvelope<string>>(totpDisablePath, { data: payload }),
      'Could not remove the authenticator.'
    )
  )
}

/** The passkeys enrolled on this account, for a management list. */
export const listPasskeys = async (): Promise<WebAuthnCredentialSummary[]> => {
  const res = await httpService.Get<{ results: WebAuthnCredentialSummary[] }>(webAuthnCredentialsPath)
  return res.results ?? []
}

/**
 * Register a passkey end to end: step-up (password) → server creation options → the browser
 * ceremony (navigator.credentials.create) → send the attestation to the server.
 */
export const useRegisterPasskey = () => {
  return useMutation([], async (payload: { password: string; label?: string }) => {
    const options = await httpService.Post<unknown>(webAuthnRegisterOptionsPath, { password: payload.password })
    const credential = await startRegistration(options)
    return unwrapResult(
      await httpService.Post<ResultEnvelope<string>>(webAuthnRegisterPath, { credential, label: payload.label }),
      'Could not add the passkey.'
    )
  })
}

/**
 * Generate (or regenerate) a batch of single-use recovery codes. Step-up (password) in the body;
 * the plaintext codes come back ONCE in `results` and are never retrievable again. Regenerating
 * invalidates any previous batch. Refused server-side unless a real strong factor is enrolled.
 */
export const useGenerateRecoveryCodes = () => {
  return useMutation([], async (payload: { password: string }) => {
    const res = await httpService.Post<ResultEnvelope<string>>(twoFactorRecoveryCodesPath, payload)
    return unwrapResult(res, 'Could not generate recovery codes.')
  })
}

/** Remove a passkey (step-up password in the body). */
export const useDeletePasskey = () => {
  return useMutation([], async (payload: { credentialId: string; password: string }) =>
    unwrapResult(
      await httpService.Delete<ResultEnvelope<string>>(webAuthnCredentialPath(payload.credentialId), {
        data: { password: payload.password },
      }),
      'Could not remove the passkey.'
    )
  )
}

/**
 * Complete a login with a passkey: fetch the assertion options for the in-progress challenge, run
 * the browser ceremony (navigator.credentials.get), and post the assertion to mint the session.
 * Anonymous/challenge-token calls — bare axios + capabilities header + withCredentials, like verify.
 */
export const useVerifyWebAuthn = () => {
  return useMutation([], async (payload: { challengeToken: string }) => {
    const anon = axios.create()
    const options = await anon
      .post<unknown>(
        webAuthnAuthenticateOptionsPath,
        { challengeToken: payload.challengeToken },
        { headers: { ...MFA_CAPABILITY_HEADERS }, withCredentials: true, timeout: 30000 }
      )
      .then((r) => r.data)
    const assertion = await startAssertion(options)
    const res = await anon
      .post<LoginResponse>(
        webAuthnVerifyPath,
        { challengeToken: payload.challengeToken, assertion },
        { headers: { ...MFA_CAPABILITY_HEADERS }, withCredentials: true, timeout: 30000 }
      )
      .then((r) => r.data)
    return res
  })
}
